import { api } from '@/lib/api';

// HW-1024: the state of a confirm link from a consent-request email. Mirrors
// ConsentLinkState in the backend's consent-events.service.ts.
export type ConsentLinkState =
  | 'PENDING'
  | 'CONFIRMED'
  // HW-1057: the consent this link gave has ended; it can still be withdrawn.
  | 'ENDED'
  | 'EXPIRED'
  | 'SUPERSEDED'
  | 'REVOKED'
  | 'NOT_FOUND';

export interface ConsentLinkResult {
  state: ConsentLinkState;
  contactEmail: string;
}

const STATES: readonly ConsentLinkState[] = [
  'PENDING',
  'CONFIRMED',
  'ENDED',
  'EXPIRED',
  'SUPERSEDED',
  'REVOKED',
  'NOT_FOUND',
];

// Read-only probe: what the link can still do. Grants nothing.
export async function getConsentLink(token: string): Promise<ConsentLinkResult> {
  const { data } = await api.get<ConsentLinkResult>(
    `/recruitment/public/consent/${encodeURIComponent(token)}`
  );
  return data;
}

// The candidate pressed "I consent". A dead link is 404/410 with the state in
// `code` — never 401 — so the axios refresh/SESSION_EXPIRED interceptor in
// lib/api.ts is not tripped.
export async function confirmConsent(token: string): Promise<ConsentLinkResult> {
  const { data } = await api.post<ConsentLinkResult>(
    `/recruitment/public/consent/${encodeURIComponent(token)}/confirm`
  );
  return data;
}

// HW-1057: the candidate pressed "Withdraw consent". Answers REVOKED, also
// when it was withdrawn already; a link that cannot withdraw is 404/409/410
// with the state in `code`, like confirm.
export async function withdrawConsent(token: string): Promise<ConsentLinkResult> {
  const { data } = await api.post<ConsentLinkResult>(
    `/recruitment/public/consent/${encodeURIComponent(token)}/withdraw`
  );
  return data;
}

/**
 * Whether a value is a link state this page knows. A newer backend may add
 * one; the page then shows its generic failure instead of crashing.
 */
export function isConsentLinkState(value: unknown): value is ConsentLinkState {
  return typeof value === 'string' && (STATES as readonly string[]).includes(value);
}

/** Whether a call was refused by the public endpoints' per-IP rate limit. */
export function isRateLimited(err: unknown): boolean {
  return (err as { response?: { status?: unknown } })?.response?.status === 429;
}

/** The link state a failed confirm reports, or null when it is not a link refusal. */
export function linkStateFromError(err: unknown): ConsentLinkState | null {
  const code = (err as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return isConsentLinkState(code) ? code : null;
}
